import React, { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Flag, CalendarClock } from "lucide-react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { flagScheduleReview, clearScheduleReview } from "../../lib/api";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    // Date-only strings (YYYY-MM-DD) render without TZ drift when parsed as local.
    const d = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(iso + "T00:00:00") : new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
};

const todayISO = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

export const ScheduleReviewBanner = ({ student, canManage, onChanged }) => {
  const sr = student?.schedule_review || {};
  const active = !!sr.active;
  const pending = !!sr.pending;
  const flaggedBy = sr.flagged_by_name || "a staff member";

  const [flagOpen, setFlagOpen] = useState(false);
  const [doneOpen, setDoneOpen] = useState(false);
  const [note, setNote] = useState("");
  const [reviewDate, setReviewDate] = useState(todayISO());
  const [nextDate, setNextDate] = useState("");
  const [saving, setSaving] = useState(false);

  const openFlag = () => {
    setNote("");
    setReviewDate(todayISO());
    setFlagOpen(true);
  };

  const doFlag = async () => {
    if (!reviewDate) {
      toast.error("Please choose a review date");
      return;
    }
    setSaving(true);
    try {
      await flagScheduleReview(student.id, note.trim(), reviewDate);
      const future = reviewDate > todayISO();
      toast.success(future
        ? `Schedule review scheduled for ${fmtDate(reviewDate)}`
        : "Schedule flagged for review — admins and clinicians notified");
      setFlagOpen(false);
      setNote("");
      onChanged && (await onChanged());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not flag schedule");
    } finally {
      setSaving(false);
    }
  };

  const doDone = async () => {
    setSaving(true);
    try {
      await clearScheduleReview(student.id, nextDate || null);
      toast.success(nextDate ? `Review cleared · next review ${fmtDate(nextDate)}` : "Review marked done");
      setDoneOpen(false);
      setNextDate("");
      onChanged && (await onChanged());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not clear review");
    } finally {
      setSaving(false);
    }
  };

  const cancelPending = async () => {
    setSaving(true);
    try {
      await clearScheduleReview(student.id, null);
      toast.success("Scheduled review cancelled");
      onChanged && (await onChanged());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel review");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {active ? (
        <div
          data-testid="schedule-review-banner"
          className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3"
        >
          <div className="flex items-start gap-3 flex-1">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-amber-900">Schedule Review Needed</p>
              {sr.note && <p className="text-amber-800 mt-0.5">{sr.note}</p>}
              <p className="text-amber-700/80 text-xs mt-1" data-testid="schedule-review-meta">
                Flagged by {flaggedBy}
                {sr.review_date
                  ? ` · Review date ${fmtDate(sr.review_date)}`
                  : (sr.flagged_at ? ` on ${fmtDate(sr.flagged_at)}` : "")}
              </p>
            </div>
          </div>
          {canManage && (
            <Button
              data-testid="mark-review-done-btn"
              className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
              onClick={() => setDoneOpen(true)}
            >
              Mark Review Done
            </Button>
          )}
        </div>
      ) : pending ? (
        <div
          data-testid="schedule-review-pending"
          className="rounded-lg border border-sky-300 bg-sky-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3"
        >
          <div className="flex items-start gap-3 flex-1">
            <CalendarClock className="h-5 w-5 text-sky-600 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-sky-900">Schedule Review Scheduled</p>
              {sr.note && <p className="text-sky-800 mt-0.5">{sr.note}</p>}
              <p className="text-sky-700/80 text-xs mt-1" data-testid="schedule-review-pending-meta">
                Review date {fmtDate(sr.review_date)} · flagged by {flaggedBy}
              </p>
            </div>
          </div>
          {canManage && (
            <Button
              variant="outline"
              size="sm"
              data-testid="cancel-scheduled-review-btn"
              className="border-sky-300 text-sky-700 hover:bg-sky-100 shrink-0"
              onClick={cancelPending}
              disabled={saving}
            >
              Cancel
            </Button>
          )}
        </div>
      ) : (
        canManage && (
          <div className="flex items-center justify-end gap-3">
            <Button
              variant="outline"
              size="sm"
              data-testid="flag-schedule-review-btn"
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              onClick={openFlag}
            >
              <Flag className="h-4 w-4 mr-1.5" />
              Flag Schedule for Review
            </Button>
          </div>
        )
      )}

      {/* Flag dialog */}
      <Dialog open={flagOpen} onOpenChange={setFlagOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Flag Schedule for Review</DialogTitle>
            <DialogDescription>
              Choose the date you want to be reminded to review this student's schedule. On that day,
              admins and clinicians get a notification and a reminder banner appears on the student's file.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="sr-review-date">Review date</Label>
              <Input
                id="sr-review-date"
                data-testid="schedule-review-date-input"
                type="date"
                value={reviewDate}
                min={todayISO()}
                onChange={(e) => setReviewDate(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">Pick today to review now, or a future date to be reminded then.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="sr-note">Reason (optional)</Label>
              <Textarea
                id="sr-note"
                data-testid="schedule-review-note-input"
                placeholder="Why does this schedule need review?"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFlagOpen(false)} disabled={saving}>Cancel</Button>
            <Button
              data-testid="confirm-flag-review-btn"
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={doFlag}
              disabled={saving}
            >
              {saving ? "Saving…" : "Set Review Reminder"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark done dialog */}
      <Dialog open={doneOpen} onOpenChange={setDoneOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Review Done</DialogTitle>
            <DialogDescription>
              This clears the review banner. To schedule the next review, set a date below and a reminder
              will surface on that day. Leave blank if no follow-up is needed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="sr-next-date">Next review date (optional)</Label>
            <Input
              id="sr-next-date"
              data-testid="next-review-date-input"
              type="date"
              value={nextDate}
              min={todayISO()}
              onChange={(e) => setNextDate(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDoneOpen(false)} disabled={saving}>Cancel</Button>
            <Button
              data-testid="confirm-review-done-btn"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={doDone}
              disabled={saving}
            >
              {saving ? "Saving…" : "Mark Done"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
